<template>
  <b-sidebar
    id="edit-pick-up-sidebar"
    :visible="isEditPickUpSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-edit-pick-up-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Edit Pick Up
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- BODY -->
      <validation-observer
        #default="{ handleSubmit, invalid }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(handleUpdatePickUp)"
        >
          <address-field-group
            :name="form.pickUpAddress.name"
            :address="form.pickUpAddress.address"
            :phone="form.pickUpAddress.phoneNumber"
            @update:name="form.pickUpAddress.name = $event"
            @update:address="form.pickUpAddress.address = $event"
            @update:phone="form.pickUpAddress.phoneNumber = $event"
          />

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="invalid"
            >
              <b-spinner
                v-if="isLoading"
                small
                class="mr-50"
              />
              {{ isLoading ? "Updating..." : "Update" }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="flat-secondary"
              @click="() => $emit('update:is-edit-pick-up-sidebar-active', false)"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
        <!-- Form Actions -->

      </validation-observer>

    </template>

  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BButton, BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { ref } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'

import store from '@/store'
import { required } from '@validations'
import phoneNumber from '@/libs/utils/validations/customValidations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddressFieldGroup from '@/components/AddressFieldGroup.vue'
import { useToast } from 'vue-toastification/composition'

export default {
  components: {
    BSidebar,
    AddressFieldGroup,
    BForm,
    BButton,
    BSpinner,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isEditPickUpSidebarActive',
    event: 'update:is-edit-pick-up-sidebar-active',
  },
  props: {
    isEditPickUpSidebarActive: {
      type: Boolean,
      required: true,
    },
    pickUpData: {
      type: Object,
      required: false,
      default: null,
    },
    jobId: {
      type: String,
      default: '',
      required: true,
    },
  },
  provide() {
    return {
      $validator: this.$validator,
    }
  },
  data() {
    return {
      pickUpAddress: '',
      fullAddress: '',
      // Validation Rules
      required,
      phoneNumber,
    }
  },

  setup(props, { emit }) {
    const toast = useToast()
    const customerOptions = ref([])
    const isLoading = ref(false)

    const form = ref({
      pickUpAddress: {
        id: null,
        name: '',
        address: '',
        phoneNumber: null,
      },
    })

    const {
      refFormObserver,
      getValidationState,
    } = formValidation()

    if (props.pickUpData) {
      form.value.pickUpAddress = { ...form.value.pickUpAddress, ...props.pickUpData }
    }

    const addPickUp = () => {
      const checkpointData = {
        jobId: props.jobId,
        checkpointData: {
          type: 'type.pick-up',
          name: form.value.pickUpAddress.name,
          address: form.value.pickUpAddress.address,
          phoneNumber: form.value.pickUpAddress.phoneNumber,
        },
      }
      store.dispatch('app-job/addJobCheckpoint', checkpointData)
        .then(response => {
          form.value.pickUpAddress = response.data.checkpoints?.find(
            checkpoint => checkpoint.type === 'type.pick-up',
          )
          emit('updateJobCanSchedule', response.data.canSchedule)
          emit('updatePickUp', form.value.pickUpAddress)
          emit('update:is-edit-pick-up-sidebar-active', false)
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Job updated',
              icon: 'CheckSquareIcon',
              variant: 'success',
              text: 'You have successfully updated the pick up checkpoint for this job.',
            },
          })
          isLoading.value = false
        })
        .catch(err => {
          Object.entries(err.response.data.errors).forEach(errors => {
            const field = errors[0]
            const messages = errors[1]
            const toUppercase = str => str.toUpperCase()
            messages.forEach(errorMessage => {
              toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: `${field.replace(/[A-Z]/g, ' $&').replace(/^./, toUppercase)}`,
                  icon: 'XIcon',
                  variant: 'warning',
                  text: errorMessage,
                },
              })
            })
          })

          emit('update:is-edit-pick-up-sidebar-active', false)
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Job update failed',
              icon: 'XIcon',
              variant: 'danger',
              text: err.response.data.message || 'An error occurred while updating the job.',
            },
          })
          isLoading.value = false
        })
    }

    const updatePickUp = () => {
      const checkpointData = {
        type: 'type.pick-up',
        name: form.value.pickUpAddress.name,
        address: form.value.pickUpAddress.address,
        phoneNumber: form.value.pickUpAddress.phoneNumber,
      }
      store.dispatch('app-checkpoint/updateJobCheckpoint', { checkpointData, checkpointId: form.value.pickUpAddress.id })
        .then(response => {
          emit('updateJobCanSchedule', response.canSchedule)
          emit('updatePickUp', form.value.pickUpAddress)
          emit('update:is-edit-pick-up-sidebar-active', false)
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Job updated',
              icon: 'CheckSquareIcon',
              variant: 'success',
              text: 'You have successfully updated the pick up checkpoint for this job.',
            },
          })
          isLoading.value = false
        })
        .catch(err => {
          Object.entries(err.response.data.errors).forEach(errors => {
            const field = errors[0]
            const messages = errors[1]
            const toUppercase = str => str.toUpperCase()
            messages.forEach(errorMessage => {
              toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: `${field.replace(/[A-Z]/g, ' $&').replace(/^./, toUppercase)}`,
                  icon: 'XIcon',
                  variant: 'warning',
                  text: errorMessage,
                },
              })
            })
          })

          emit('update:is-edit-pick-up-sidebar-active', false)
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Job update failed',
              icon: 'XIcon',
              variant: 'danger',
              text: err.response.data.message || 'An error occurred while updating the job.',
            },
          })
          isLoading.value = false
        })
    }

    const handleUpdatePickUp = () => {
      isLoading.value = true
      if (form.value.pickUpAddress.id !== null) {
        return updatePickUp()
      }
      return addPickUp()
    }

    return {
      customerOptions,
      refFormObserver,
      getValidationState,
      form,
      handleUpdatePickUp,
      isLoading,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#edit-pick-up-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
